import { z } from 'zod';

/**
 * Client-side mirror of the backend import mapping + transform model
 * (`packages/nest-backend/src/imports/dto/mapping.dto.ts` +
 * `pipeline/transforms.ts`). The backend is the source of truth: it both
 * validates AND applies the mapping. This module reproduces the transform
 * vocabulary and value semantics byte-for-byte so the wizard's client-side
 * preview agrees with what the server will do. The shared JSON fixture set
 * (`excel-import/__fixtures__/import-cases.json`, copied from nest-backend) is
 * asserted against both implementations to guard against divergence.
 */

/**
 * How a single spreadsheet column's raw cell is transformed into the string
 * value fed to the importer. `whole` reproduces the legacy `row[header]`
 * behaviour exactly, so pre-existing mappings are unaffected.
 */
export const ImportTransformSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('whole'),
    header: z.string().min(1),
    /** Cell values treated as blank/unset (case-insensitive, trimmed). */
    blanks: z.array(z.string()).optional(),
  }),
  z.object({
    kind: z.literal('first_n'),
    header: z.string().min(1),
    n: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('last_n'),
    header: z.string().min(1),
    n: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('after_n'),
    header: z.string().min(1),
    n: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('concat'),
    headers: z.array(z.string().min(1)).min(2),
    separator: z.string(),
  }),
  z.object({
    kind: z.literal('static'),
    /** A constant value applied to every row — no source column is read. */
    value: z.string(),
  }),
]);
export type ImportTransform = z.infer<typeof ImportTransformSchema>;

/**
 * One mapping entry: a transform that yields the raw value and the grid target
 * it writes to. `targetField === ''` means "ignore this column".
 *
 * `targetField` addresses:
 *   - ''                    → ignore
 *   - 'work_order_name'     → identity (match key)
 *   - <container_key>       → DFMA container option
 *   - <work_order_field>    → structured scalar column
 *   - 'metadata.<key>'      → custom (metadata) column
 *   - 'project.code' / 'project.name' → consumed by the server's project
 *                             resolver, not by validateImport.
 *   - 'product.code' / 'product.name' → resolved to a partner Product, which
 *                             then supplies the row's container options.
 */
export const ImportMappingEntrySchema = z.object({
  targetField: z.string(),
  transform: ImportTransformSchema,
  /** number_with_unit target — the unit the whole column is entered in. */
  unit: z.string().optional(),
});
export type ImportMappingEntry = z.infer<typeof ImportMappingEntrySchema>;

export const ImportMappingSchema = z.array(ImportMappingEntrySchema);
export type ImportMapping = z.infer<typeof ImportMappingSchema>;

/** Special targets resolved by the server's project resolver, not validateImport. */
export const PROJECT_CODE_TARGET = 'project.code';
export const PROJECT_NAME_TARGET = 'project.name';

/**
 * Synthetic targets naming an existing partner Product directly, instead of
 * spelling out the container options that would work one out. `product.code`
 * matches on `products.product_key` (the "Product Code"), `product.name` on
 * `products.product_name`.
 *
 * Mapping either one takes over container resolution for the whole file: the
 * resolved product's container selections are expanded into exactly the
 * container edits a fully-mapped container import would have produced, so
 * everything downstream (Product Type gating, editability, product rebinding)
 * is unchanged. The two are mutually exclusive, and any container entry left in
 * the mapping is ignored while one is set.
 *
 * Mirrors `nest-backend/src/imports/dto/mapping.dto.ts`.
 */
export const PRODUCT_CODE_TARGET = 'product.code';
export const PRODUCT_NAME_TARGET = 'product.name';

/**
 * Pseudo-field addressing a work order's `product_id` directly. Emitted only
 * for a resolved product that carries NO container selections (a legacy
 * product, hand-curated Bill of Operations) — there are no container options to
 * expand, so the bind is the only way to express it. Never a real grid column.
 */
export const PRODUCT_ID_FIELD = 'product_id';

/**
 * Synthetic target carrying the status new work orders should be created in
 * (`'draft'` | `'scheduled'`), stored as a `static` transform. Consumed by the
 * import processor (only Automatic/Drive imports honour `'scheduled'`), never a
 * real work-order column, so `validateImport` ignores it like the project.* targets.
 */
export const WORK_ORDER_STATUS_TARGET = 'work_order_status';
export type NewWorkOrderStatus = 'draft' | 'scheduled';

/** A parsed spreadsheet row, keyed by header. */
export type ParsedRow = Record<string, unknown>;

/** Safe coercion of an unknown value to a string — objects/arrays become ''. */
export function safeString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

export function isBlank(raw: unknown): boolean {
  return raw === null || raw === undefined || safeString(raw).trim() === '';
}

/** `YYYY-MM-DD` in local time — mirrors the backend transforms' date formatting. */
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Trimmed string form of a spreadsheet cell (numbers/dates coerced sensibly):
 *   - Date  → `YYYY-MM-DD` (falls back to full ISO on an un-formattable date)
 *   - number/boolean → `String(...)`
 *   - null/undefined/object/array → ''
 *
 * Mirrors the backend importer's `cellString` (and the legacy wizard's) so the
 * transform layer keeps identical value semantics on both sides of the wire.
 */
export function cellString(raw: unknown): string {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? raw.toISOString() : formatLocalDate(raw);
  }
  return safeString(raw).trim();
}

/**
 * A whole-cell value, blanked to '' when it matches one of `blanks`
 * (case-insensitive, trimmed; empty entries ignored). Lets an import treat
 * sentinel values like "NONE" / "No" as an unset cell so the target clears.
 */
export function applyBlanks(value: string, blanks: readonly string[] | undefined): string {
  if (!blanks || blanks.length === 0) return value;
  const needle = value.trim().toLowerCase();
  if (needle === '') return value;
  return blanks.some((b) => b.trim().toLowerCase() === needle) ? '' : value;
}

/**
 * Resolve a mapping entry's raw string value from a parsed row.
 *
 * `whole` equals the legacy `row[header]` behaviour when no `blanks` are
 * configured, so pre-existing mappings are unaffected. The slice-based kinds
 * operate on the cell's `cellString` form; `concat` joins several columns with
 * the separator. This is a byte-for-byte port of
 * `nest-backend/src/imports/pipeline/transforms.ts:applyTransform`.
 */
export function applyTransform(t: ImportTransform, row: ParsedRow): string {
  const s = (h: string) => cellString(row[h]);
  switch (t.kind) {
    case 'whole':
      return applyBlanks(s(t.header), t.blanks);
    case 'first_n':
      return s(t.header).slice(0, t.n);
    case 'last_n':
      return s(t.header).slice(-t.n);
    case 'after_n':
      return s(t.header).slice(t.n);
    case 'concat':
      return t.headers.map(s).join(t.separator);
    case 'static':
      return t.value;
  }
}
